import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AI_IMPORT_MAX_RUNNING, forgetAiRun, readAiRunIds } from '../utils/aiImportRun';
import { fetchRunStatus, TRunStatus } from '../utils/aiImportRunApi';

// How often running imports are asked how far they are.
export const TRACKED_POLL_INTERVAL_MS = 4_000;

/**
 * The AI imports this browser keeps (a superuser's list, utils/aiImportRun.ts), newest first, with
 * what the server says of each: running ones are asked again every few seconds, finished ones once.
 * An import the server no longer has (404) leaves the list.
 */
const useTrackedAiRuns = (enabled: boolean, getToken: () => string | undefined) => {
  const [runIds, setRunIds] = useState<string[]>(() => (enabled ? readAiRunIds() : []));
  const [statuses, setStatuses] = useState<Record<string, TRunStatus>>({});
  // Started from this page and not told of yet: running, for the limit, until its status comes.
  const [started, setStarted] = useState<string[]>([]);
  const statusesRef = useRef(statuses);
  statusesRef.current = statuses;
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const poll = useCallback(async (all = false) => {
    const ids = readAiRunIds();
    setRunIds(ids);
    await Promise.all(
      ids.map(async runId => {
        const known = statusesRef.current[runId];
        if (!all && known && known.status !== 'running') return;
        try {
          const status = await fetchRunStatus(runId, getTokenRef.current(), { summary: true });
          setStatuses(previous => ({ ...previous, [runId]: status }));
        } catch (err: any) {
          if (err?.response?.status === 404) {
            forgetAiRun(runId);
            setRunIds(readAiRunIds());
          }
        }
      })
    );
  }, []);

  useEffect(() => {
    if (!enabled) return;
    poll(true);
    const timer = setInterval(() => poll(), TRACKED_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [enabled, poll]);

  // Takes an import out of the list (it goes on, and stays in the statistics).
  const forget = useCallback((runId: string) => {
    forgetAiRun(runId);
    setRunIds(readAiRunIds());
  }, []);

  const runs = useMemo(() => runIds.map(runId => ({ runId, status: statuses[runId] ?? null })), [runIds, statuses]);
  const runningCount = runs.filter(
    ({ runId, status }) => status?.status === 'running' || (!status && (started.includes(runId) || runIds.includes(runId)))
  ).length;
  // An import just started (stored with storeAiRunId): it counts as running at once, then its status.
  const added = useCallback(
    (runId: string) => {
      setStarted(previous => [...previous, runId]);
      setRunIds(readAiRunIds());
      poll(true);
    },
    [poll]
  );
  // After a start, a cancel or an import: the list and every status again.
  const refresh = useCallback(() => {
    poll(true);
  }, [poll]);

  return useMemo(
    () => ({ runs, runningCount, atLimit: runningCount >= AI_IMPORT_MAX_RUNNING, refresh, forget, added }),
    [runs, runningCount, refresh, forget, added]
  );
};

export default useTrackedAiRuns;
